import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Guests = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchGuests();
  }, []);

  const fetchGuests = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/guests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGuest = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/guests`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Guest created successfully');
      setDialogOpen(false);
      setFormData({ name: '', email: '', phone: '', notes: '' });
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create guest');
    }
  };

  const filteredGuests = guests.filter(guest => {
    const search = searchTerm.toLowerCase();
    return guest.name.toLowerCase().includes(search) ||
           guest.email.toLowerCase().includes(search) ||
           guest.phone.toLowerCase().includes(search);
  });

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="guests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Guests</h1>
          <p className="text-slate-600 mt-1">{filteredGuests.length} guest(s)</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-guest-button">
              <Plus size={18} />
              Add Guest
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Guest</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateGuest} className="space-y-4" data-testid="guest-form">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Doe"
                  required
                  data-testid="guest-name-input"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@example.com"
                  data-testid="guest-email-input"
                />
              </div>

              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1 234 567 8900"
                  data-testid="guest-phone-input"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Special requests, preferences, etc."
                />
              </div>

              <Button type="submit" className="w-full" data-testid="submit-guest-button">
                Add Guest
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          placeholder="Search guests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="guest-search-input"
        />
      </div>

      <Card data-testid="guests-list">
        <CardContent className="p-0">
          {filteredGuests.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {searchTerm ? 'No guests found' : 'No guests yet. Add your first guest.'}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredGuests.map((guest) => (
                <div
                  key={guest.id}
                  className="p-5 hover:bg-slate-50 transition-colors"
                  data-testid={`guest-${guest.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-semibold text-lg">
                        {guest.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{guest.name}</h3>
                        <div className="mt-2 space-y-1">
                          {guest.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Mail size={14} />
                              <span>{guest.email}</span>
                            </div>
                          )}
                          {guest.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Phone size={14} />
                              <span>{guest.phone}</span>
                            </div>
                          )}
                          {guest.notes && (
                            <p className="text-sm text-slate-500 mt-2">{guest.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Guests;
